// Refinery progress card — polls /api/refinery/jobs/{id} every 4s, shows live
// progress bar, cost-so-far, current chunk, cancel button, and the streaming
// staging buffer. Used by both the Workbench's "Refine continuously" mode
// and the silo Operations tab's "Refine Silo" panel.
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { X, Pause, CheckCircle, Warning, FloppyDisk, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 4000;
const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function RefineryProgressCard({ jobId, onComplete, onClose }) {
  const [job, setJob] = useState(null);
  const [polling, setPolling] = useState(true);
  const stagingRef = useRef(null);
  // Guard against onComplete firing more than once. Without this, parents that
  // pass an inline arrow function (Workbench, FileRefinery) re-render on
  // setStaging → new onComplete reference → effect re-runs → tick() sees
  // terminal status → onComplete fires AGAIN → infinite loop that produces
  // 100s of toast popups + a staging buffer that grows by Nx its true size.
  const completedFiredRef = useRef(false);

  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get(`/refinery/jobs/${jobId}`);
        if (!alive) return;
        setJob(r.data);
        if (["completed", "completed_with_gaps", "cancelled", "error"].includes(r.data.status)) {
          setPolling(false);
          if ((r.data.status === "completed" || r.data.status === "completed_with_gaps") && !completedFiredRef.current) {
            completedFiredRef.current = true;
            onComplete?.(r.data);
          }
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setPolling(false);
        }
      }
    };
    tick();
    const id = setInterval(() => { if (polling) tick(); }, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
    // onComplete intentionally excluded from deps — parents pass a fresh inline
    // arrow each render; including it caused the duplication-explosion bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, polling]);

  // Auto-scroll the staging viewer as new chunks land
  useEffect(() => {
    if (!stagingRef.current) return;
    if (job?.status === "running") stagingRef.current.scrollTop = stagingRef.current.scrollHeight;
  }, [job?.staging_buffer, job?.status]);

  const cancel = async () => {
    if (!window.confirm("Cancel this refinery job? Progress so far will be saved to staging.")) return;
    try {
      await api.post(`/refinery/jobs/${jobId}/cancel`);
      toast.success("Cancellation requested.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel.");
    }
  };

  if (!job) return <div className="text-sm text-[#6B6A68]">Loading job…</div>;

  const isTerminal = ["completed", "completed_with_gaps", "cancelled", "error"].includes(job.status);
  const statusStyles = {
    queued: { bg: "bg-[#F0F1EE]", text: "text-[#6B6A68]", label: "Queued" },
    running: { bg: "bg-[#FFF9E6]", text: "text-[#8A5A00]", label: "Running" },
    completed: { bg: "bg-[#D4EDDA]", text: "text-[#2F6B3A]", label: "Completed" },
    completed_with_gaps: { bg: "bg-[#FFF9E6]", text: "text-[#8A5A00]", label: "Completed (some chunks skipped)" },
    cancelled: { bg: "bg-[#F0F1EE]", text: "text-[#6B6A68]", label: "Cancelled" },
    error: { bg: "bg-[#FDF1ED]", text: "text-[#C16E5A]", label: "Error" },
  };
  const sty = statusStyles[job.status] || statusStyles.queued;

  return (
    <div className="bg-white border hairline rounded-lg p-5" data-testid="refinery-progress-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif-display text-lg text-[#1C1B1A]">Refinery job</h3>
            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded ${sty.bg} ${sty.text}`} data-testid="refinery-status">
              {sty.label}
            </span>
          </div>
          <div className="text-[10px] text-[#6B6A68] mt-1 font-mono">
            {job.cmd_name} · job {jobId.slice(0, 8)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isTerminal && (
            <button onClick={cancel} data-testid="refinery-cancel"
              className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1">
              <Pause size={12} /> Cancel
            </button>
          )}
          {isTerminal && onClose && (
            <button onClick={onClose} data-testid="refinery-close" className="p-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-baseline justify-between mb-1.5 text-xs">
          <span className="text-[#1C1B1A] tabular-nums" data-testid="refinery-progress-text">
            Chunk <strong>{job.chunks_completed || 0}</strong> of <strong>{(job.chunks_plan?.length) || job.chunk_count || (job.chars_total ? "—" : 0)}</strong>
          </span>
          <span className="text-[#6B6A68] tabular-nums">{(job.progress_pct || 0).toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#F0F1EE] overflow-hidden">
          <motion.div
            className={`h-full ${job.status === "error" ? "bg-[#C16E5A]" : "bg-[#4A5D4E]"}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, job.progress_pct || 0)}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="refinery-progress-bar"
          />
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-3 gap-2 text-xs mb-3">
        <Stat label="Chars processed" value={`${(job.chars_processed || 0).toLocaleString()} / ${(job.chars_total || 0).toLocaleString()}`} />
        <Stat label="Cost so far" value={fmt$(job.actual_cost_cents)} hint={`est. ${fmt$(job.est_cost_cents)}`} />
        <Stat label="Cap" value={fmt$(job.hard_cap_cents)} />
      </div>

      {job.status === "completed_with_gaps" && (job.chunks_skipped || 0) > 0 && (
        <div className="bg-[#FFF9E6] text-[#8A5A00] border border-[#E5B866] rounded p-2.5 text-xs flex items-start gap-1.5 mb-3" data-testid="refinery-partial-notice">
          <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
          <span>
            <strong>{job.chunks_skipped} of {(job.chunks_plan?.length) || "—"} chunks</strong> were
            skipped due to transient Gemini 503s. Successful chunks are in staging below. Re-run the
            refinery when Google stabilizes — or scroll the staging buffer for <code>[SKIPPED]</code>{" "}
            markers, grab those char ranges, and use the Workbench's "Skip ahead" button to just
            re-process the gaps.
          </span>
        </div>
      )}

      {job.status === "error" && job.error && (
        <div className="bg-[#FDF1ED] text-[#C16E5A] border border-[#C16E5A] rounded p-2.5 text-xs flex items-start gap-1.5 mb-3" data-testid="refinery-error">
          <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
          <span>{job.error}</span>
        </div>
      )}

      {job.auto_committed_doc_id && (
        <div className="bg-[#D4EDDA] text-[#2F6B3A] border border-[#2F6B3A] rounded p-2.5 text-xs flex items-start gap-1.5 mb-3" data-testid="refinery-auto-committed">
          <CheckCircle size={14} weight="duotone" className="shrink-0 mt-0.5" />
          <span>Auto-committed to your silo as a new Tier {job.auto_commit_to_tier} document. Originals untouched.</span>
        </div>
      )}

      {/* Live staging viewer */}
      <details className="mt-3" open={isTerminal}>
        <summary className="text-xs uppercase tracking-[0.18em] text-[#6B6A68] cursor-pointer">
          Streaming staging buffer · {((job.staging_buffer || "").length).toLocaleString()} chars
        </summary>
        <textarea
          ref={stagingRef}
          readOnly
          value={job.staging_buffer || ""}
          rows={12}
          data-testid="refinery-staging-buffer"
          className="w-full mt-2 bg-[#FDFCF8] border hairline rounded-md px-3 py-2 font-mono text-xs"
          placeholder="Results will stream here as the AI processes each chunk…"
        />
        {isTerminal && job.staging_buffer && (
          <div className="flex items-center justify-end gap-2 mt-2 flex-wrap">
            <button
              onClick={async () => {
                // Feb 17, 2026 — navigator.clipboard.writeText silently fails
                // inside the hosting preview iframe (no permission). Use the
                // copyText fallback util that creates a hidden textarea + execCommand
                // so it works in both iframe and standalone contexts. Also surface
                // success / failure as a toast so the user gets feedback.
                try {
                  const mod = await import("@/lib/io");
                  const ok = await mod.copyText(job.staging_buffer);
                  toast[ok ? "success" : "error"](
                    ok ? "Copied to clipboard." : "Couldn't copy — your browser blocked it. Try the Download button."
                  );
                } catch {
                  toast.error("Copy failed.");
                }
              }}
              data-testid="refinery-copy"
              className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-[#4A5D4E]">
              Copy result
            </button>
            <button onClick={() => {
              const blob = new Blob([job.staging_buffer], { type: "text/plain" });
              const u = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = u; a.download = `refinery-${jobId.slice(0,8)}.txt`;
              a.click(); URL.revokeObjectURL(u);
            }} data-testid="refinery-download"
              className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1">
              <FloppyDisk size={12} /> Download .txt
            </button>
            {job.silo_id && (
              <button
                onClick={async () => {
                  try {
                    // Fetch current Workbench staging, append this job's buffer, persist.
                    const cur = await api.get(`/silos/${job.silo_id}/workbench-state`);
                    const existing = cur.data?.staging || "";
                    const next = existing
                      ? `${existing}\n\n${job.staging_buffer}`
                      : job.staging_buffer;
                    await api.patch(`/silos/${job.silo_id}/workbench-state`, { staging: next });
                    toast.success("Sent to Workbench staging area.");
                  } catch (e) {
                    // detail is now normalized to a string by the axios interceptor
                    toast.error(e?.response?.data?.detail || e?.message || "Could not send to staging.");
                  }
                }}
                data-testid="refinery-send-to-staging"
                title="Append this result into the Workbench staging area for this silo. Useful for recovering output from a job whose page you closed."
                className="text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1"
              >
                <ArrowUp size={12} weight="bold" /> Send to Workbench staging
              </button>
            )}
          </div>
        )}
      </details>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="border hairline rounded-md p-2">
      <div className="text-[9px] uppercase tracking-[0.18em] text-[#6B6A68]">{label}</div>
      <div className="text-sm text-[#1C1B1A] tabular-nums font-medium">{value}</div>
      {hint && <div className="text-[9px] text-[#6B6A68]">{hint}</div>}
    </div>
  );
}
